// CatalogueItemModal.jsx
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Pencil } from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const CatalogueItemModal = ({
  viewModalItem,
  setViewModalItem,
  viewModalTab,
  setViewModalTab,
  editableDetails,
  setEditableDetails,
  isEditingDetails,
  setIsEditingDetails,
  handleNumberWheel,

  // availability
  availabilityData,
  handleCheckboxChange,
  handleSubmit,

  // printer
  printerList,
  printerLoading,
  fetchPrinterList,
  selectedPrinter,
  setSelectedPrinter,
  setAddPrinterModalOpen,
  handleSavePrinterToItem,

  // routing + API
  navigate,
  userID,
  baseUrl,
  config,

  // refresh list after update
  onItemUpdated,

  // variants master: [{ pk_variant_id, variant_title, category }]
  variants,
  loadingVariants,
}) => {
  const [subCategories, setSubCategories] = useState([]);
  const [rackList, setRackList] = useState([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Normalise variants to always be an array
  const variantList = Array.isArray(variants)
    ? variants
    : variants
      ? [variants]
      : [];

  // ---------- DATE HELPERS (expiry) ----------

  const parseBackendDateToInput = (raw) => {
    if (!raw) return "";
    let year, month, day;

    // 11/15/2025 12:00:00 AM style
    const m = raw.match(/^\s*(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) {
      month = Number(m[1]);
      day = Number(m[2]);
      year = Number(m[3]);
    } else {
      const dt = new Date(raw);
      if (Number.isNaN(dt.getTime())) return "";
      year = dt.getFullYear();
      month = dt.getMonth() + 1;
      day = dt.getDate();
    }

    const mm = month < 10 ? `0${month}` : String(month);
    const dd = day < 10 ? `0${day}` : String(day);
    return `${year}-${mm}-${dd}`; // HTML date input format
  };

  const toPayloadDate = (inputVal, fallbackRaw) => {
    if (inputVal) return inputVal.replace(/-/g, "/"); // yyyy/mm/dd
    const norm = parseBackendDateToInput(fallbackRaw);
    return norm ? norm.replace(/-/g, "/") : "";
  };

  // Normalize initial expiry to YYYY-MM-DD
  useEffect(() => {
    if (!viewModalItem) return;
    setEditableDetails((prev) => {
      const src = prev?.expiryDate || viewModalItem.expiryDate;
      const norm = parseBackendDateToInput(src);
      return { ...(prev || {}), expiryDate: norm };
    });
  }, [viewModalItem, setEditableDetails]);

  // ---------- IMAGE HELPERS ----------

  const uploadImageIfNeeded = async () => {
    const img = editableDetails?.imageUrl;

    // If already a URL (not base64), just reuse it
    if (img && !img.startsWith("data:")) return img || "";

    // If nothing or not base64, fall back to original photo
    if (!img?.startsWith?.("data:")) {
      return (
        editableDetails?.photo ||
        viewModalItem?.photo ||
        ""
      );
    }

    // Upload base64
    const requestBody = {
      userid: String(userID || "admin"),
      item_name: editableDetails?.item_name || "Item",
      type: "IMG",
      image_data: img,
    };

    const resp = await axios.post(
      "https://bumppy.in/anlook/api/v1/uploadItemImage.php",
      requestBody,
      config
    );

    if (resp?.data?.status === "0" && resp?.data?.image_url) {
      return resp.data.image_url;
    }

    throw new Error(resp?.data?.message || "Image upload failed");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditableDetails((prev) => ({
        ...prev,
        imageUrl: reader.result, // base64
      }));
    };
    reader.readAsDataURL(file);
  };

  // ---------- META: SUB-CATEGORIES & RACKS ----------

  useEffect(() => {
    if (!isEditingDetails || !viewModalItem) return;

    const loadMeta = async () => {
      setMetaLoading(true);
      try {
        // 1) All sub-categories
        const subRes = await axios.post(
          `${baseUrl}/api/Inventory/v1.0/getInventorySubCategories`,
          {
            userid: userID,
            category: "",
          },
          config
        );

        if (subRes.data.responseCode === "00") {
          const list = subRes.data.response || [];
          setSubCategories(list);

          // ✅ Get subCategoryId from item (category is '["2246"]')
          let itemSubCatId = "";
          try {
            const arr = JSON.parse(viewModalItem.category || "[]");
            itemSubCatId = Array.isArray(arr) && arr.length ? String(arr[0]) : "";
          } catch (e) {
            itemSubCatId = "";
          }

          const matched = list.find((sc) => String(sc.id) === String(itemSubCatId)) || null;

          setEditableDetails((prev) => ({
            ...prev,
            // store display name separately
            category: prev.category || matched?.category || "",
            // store id (this is what you should bind to select)
            categoryId: prev.categoryId || matched?.id || itemSubCatId || "",
          }));
        } else {
          toast.error(
            subRes.data.responseMessage || "Failed to load sub-categories"
          );
        }

        // 2) All racks
        const rackRes = await axios.post(
          `${baseUrl}/api/Inventory/v1.0/getRackDetails`,
          { userid: userID },
          config
        );

        if (rackRes.data.responseCode === "00") {
          const list = rackRes.data.response || [];
          setRackList(list);

          const itemRack = viewModalItem.rackNumber || "";
          const matchedRack =
            list.find((r) => r.rackNumber === itemRack) ||
            list[0] ||
            null;

          setEditableDetails((prev) => ({
            ...prev,
            rackNo:
              prev.rackNo ||
              matchedRack?.rackNumber ||
              itemRack ||
              "",
          }));
        } else {
          toast.error(
            rackRes.data.responseMessage || "Failed to load rack details"
          );
        }
      } catch (err) {
        console.error(err);
        toast.error("Error loading categories / rack details");
      } finally {
        setMetaLoading(false);
      }
    };

    loadMeta();
  }, [
    isEditingDetails,
    viewModalItem,
    baseUrl,
    config,
    userID,
    setEditableDetails,
  ]);

  // ---------- VARIANT PREFILL (from price row + variants master) ----------

  useEffect(() => {
    if (!viewModalItem || variantList.length === 0) {
      return;
    }

    setEditableDetails((prev) => {
      const prevObj = prev || {};
      // If already have variant info, don't override user edits
      if (prevObj.variantId || prevObj.variantName) return prevObj;

      const idx = prevObj.selectedPriceIndex ?? 0;
      const priceRow =
        viewModalItem.prices?.[idx] ||
        viewModalItem.prices?.[0] ||
        {};

      // From price row: variantId/variantName or fallback to size_id/size
      let variantId = priceRow.variantId || priceRow.size_id || "";
      let variantName = priceRow.variantName || priceRow.size || "";

      // If we have id but no name, try to resolve from variants master
      if (variantId && !variantName) {
        const matchById = variantList.find(
          (v) => String(v.pk_variant_id) === String(variantId)
        );
        if (matchById) {
          variantName = matchById.variant_title || variantName;
        }
      }

      // If we have name but no id, try to resolve id from master
      if (!variantId && variantName) {
        const matchByTitle = variantList.find(
          (v) => v.variant_title === variantName
        );
        if (matchByTitle) {
          variantId = matchByTitle.pk_variant_id || variantId;
        }
      }

      return {
        ...prevObj,
        variantId: variantId
          ? String(variantId)
          : prevObj.variantId || "",
        variantName: variantName || prevObj.variantName || "",
      };
    });
  }, [viewModalItem, variantList, setEditableDetails]);

  // ---------- SAVE ITEM (FULL PAYLOAD) ----------

  const handleUpdateItem = async () => {
    if (!editableDetails || !viewModalItem) return;

    setSaving(true);
    try {
      const selectedIdx = editableDetails.selectedPriceIndex ?? 0;
      const selectedPriceRow =
        viewModalItem.prices?.[selectedIdx] ||
        viewModalItem.prices?.[0] ||
        {};

      const finalImageUrl = await uploadImageIfNeeded();
      const expiryForPayload = toPayloadDate(
        editableDetails.expiryDate,
        viewModalItem.expiryDate
      );
      const selectedSubCat =
        subCategories.find((sc) => String(sc.id) === String(editableDetails.categoryId)) || null;

      const payload = {
        userid: String(userID),

        itemId: String(editableDetails.item_id || ""),
        itemName: String(editableDetails.item_name || ""),
        desc: String(editableDetails.item_description || ""),

        sizeId: String(editableDetails.sizeId || ""),

        // Sub-category name ("Kids", "Compact", etc.)
        category: String(selectedSubCat?.category || ""),

        // Size string (can be variant title like "New Game")
        size: String(editableDetails.size || ""),

        sellingPrice: String(editableDetails.sellingPrice ?? ""),
        homeDeliveryPrice: String(
          editableDetails.homeDeliveryPrice ?? ""
        ),
        takewayPrice: String(editableDetails.takewayPrice ?? ""),
        diningPrice: String(editableDetails.diningPrice ?? ""),
        roomOrder: String(editableDetails.roomOrderPrice ?? ""),
        carOrder: String(editableDetails.carOrderPrice ?? ""),


        taxPercentage: String(
          editableDetails.taxPercentage ?? ""
        ),

        id: String(
          editableDetails.id ||
          selectedPriceRow.id ||
          ""
        ),

        image: String(
          finalImageUrl ||
          editableDetails.photo ||
          viewModalItem.photo ||
          ""
        ),

        // Sub-category ID from getInventorySubCategories
        categoryId: String(editableDetails.categoryId || ""),

        // Rack number (e.g. "1")
        rackNo: String(
          editableDetails.rackNo ||
          viewModalItem.rackNumber ||
          ""
        ),

        // yyyy/mm/dd
        expiryDate: expiryForPayload,

        // Variant info (from state first, then price row)
        variantName: String(
          editableDetails.variantName ||
          selectedPriceRow.variantName ||
          editableDetails.size ||
          ""
        ),
        variantId: String(
          editableDetails.variantId ||
          selectedPriceRow.variantId ||
          editableDetails.sizeId ||
          ""
        ),

      };

      const res = await axios.post(
        `${baseUrl}/api/Order/v1.0/updateCatalogItems`,
        payload,
        config
      );

      if (res.data.responseCode === "00") {
        toast.success(
          res.data.responseMessage || "Item updated successfully"
        );
        setIsEditingDetails(false);
        onItemUpdated && onItemUpdated();
      } else {
        Swal.fire(
          "Error",
          res.data.responseMessage || "Update failed",
          "error"
        );
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err.message || "Something went wrong", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!viewModalItem) return null;

  const previewImage =
    editableDetails.imageUrl ||
    editableDetails.photo ||
    viewModalItem.photo;

  const expiryDisplay =
    editableDetails.expiryDate
      ? editableDetails.expiryDate.replace(/-/g, "/")
      : toPayloadDate("", viewModalItem.expiryDate);

  return (
    <Modal
      show={!!viewModalItem}
      onHide={() => setViewModalItem(null)}
      centered
      size="lg"
      backdrop="static"
      className="view-item-modal"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="fw-bold">
          {viewModalItem.item_name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{ minHeight: "400px", maxHeight: "600px", overflowY: "auto" }}
      >
        <div className="tab-header mb-3">
          {["details", "availability", "printer"].map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${viewModalTab === tab ? "active" : ""}`}
              onClick={() => {
                setViewModalTab(tab);
                if (tab === "printer") fetchPrinterList();
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {/* DETAILS TAB */}
          {viewModalTab === "details" && (
            <>
              <div className="d-flex justify-content-between align-items-start">
                <h5 className="mb-3 fw-semibold">Item Overview</h5>
                <button
                  className="btn btn-sm d-flex align-items-center gap-1 text-primary bg-transparent border-0"
                  onClick={() =>
                    setIsEditingDetails(!isEditingDetails)
                  }
                >
                  <Pencil size={16} />
                  {isEditingDetails ? "Cancel" : "Edit"}
                </button>
              </div>

              {metaLoading && isEditingDetails && (
                <div className="alert alert-info py-2 mb-3">
                  Loading sub-categories & rack details...
                </div>
              )}

              <div className="d-flex gap-4 flex-wrap">
                {/* IMAGE + UPLOAD */}
                <div className="d-flex">
                  <div
                    style={{
                      width: "200px",
                      height: "200px",
                      borderRadius: "10px",
                      overflow: "hidden",
                    }}
                  >
                    <img
                      src={previewImage}
                      alt={editableDetails.item_name}
                      className="w-100 h-100 object-fit-cover border"
                    />

                  </div>
                  {!isEditingDetails && editableDetails.item_description && <div className=" w-50 h-100 ms-3 p-3 rounded-3 border bg-light text-secondary small fw-semibold">
                    <p className="text-dark">
                      {!isEditingDetails ? editableDetails.item_name : ""} <br /> <hr className="p-0 m-0" />

                    </p>
                    <p >

                      {!isEditingDetails ? editableDetails.item_description : ""}
                    </p>
                  </div>}




                </div>



                <div className="flex-grow-1">
                  {isEditingDetails && (
                    <div className="mb-2">
                      <label className="form-label">
                        Update Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={handleImageChange}
                      />
                      <small className="text-muted">
                        Choose a new image to override the current one.
                      </small>
                    </div>
                  )}

                  {isEditingDetails ? (
                    <>
                      {/* Item Name */}
                      <div className="mb-2">
                        <label className="form-label">Item Name</label>
                        <input
                          className="form-control"
                          value={editableDetails.item_name}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              item_name: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Description</label>
                        <input
                          className="form-control"
                          value={editableDetails.item_description}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              item_description: e.target.value,
                            })
                          }
                        />
                      </div>

                      {/* Sub-category (from getInventorySubCategories) */}
                      <div className="mb-2">
                        <label className="form-label">
                          Sub-category
                        </label>
                        <select
                          className="form-select"
                          value={editableDetails.categoryId || ""}
                          onChange={(e) => {
                            const id = e.target.value;
                            const sel = subCategories.find((sc) => String(sc.id) === String(id)) || null;

                            setEditableDetails((prev) => ({
                              ...prev,
                              categoryId: id,
                              category: sel?.category || "", // display name
                            }));
                          }}
                        >
                          <option value="">Select sub-category</option>
                          {subCategories.map((sc) => (
                            <option key={sc.id} value={sc.id}>
                              {sc.category}
                            </option>
                          ))}
                        </select>

                      </div>

                      {/* Rack */}
                      <div className="mb-2">
                        <label className="form-label">
                          Rack Number
                        </label>
                        <select
                          className="form-select"
                          value={editableDetails.rackNo || ""}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              rackNo: e.target.value,
                            })
                          }
                        >
                          <option value="">Select rack</option>
                          {rackList.map((rack) => (
                            <option
                              key={rack.id}
                              value={rack.rackNumber}
                            >
                              {`Rack ${rack.rackNumber} – ${rack.category}${Array.isArray(rack.tags) &&
                                rack.tags.length
                                ? ` (${rack.tags.join(", ")})`
                                : ""
                                }`}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Variant (master list) */}
                      <div className="mb-2">
                        <label className="form-label">Variant</label>
                        {loadingVariants ? (
                          <div className="small text-muted">
                            Loading variants...
                          </div>
                        ) : (
                          <select
                            className="form-select"
                            value={editableDetails.variantId || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              const sel =
                                variantList.find(
                                  (v) =>
                                    String(v.pk_variant_id) ===
                                    String(val)
                                ) || null;
                              setEditableDetails((prev) => ({
                                ...prev,
                                variantId: val,
                                variantName:
                                  sel?.variant_title ||
                                  prev.variantName ||
                                  "",
                              }));
                            }}
                          >
                            <option value="">Select variant</option>
                            {variantList.map((v) => (
                              <option
                                key={v.pk_variant_id}
                                value={v.pk_variant_id}
                              >
                                {v.variant_title}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Size / price row */}
                      <div className="mb-2">
                        <label className="form-label">
                          Size / Price Row
                        </label>
                        <select
                          className="form-select"
                          value={editableDetails.selectedPriceIndex ?? 0}
                          onChange={(e) => {
                            const idx = Number(e.target.value);
                            const p =
                              viewModalItem.prices?.[idx] || {};
                            setEditableDetails((prev) => ({
                              ...prev,
                              selectedPriceIndex: idx,
                              sizeId: p.size_id || "",
                              size: p.size || "",
                              sellingPrice: p.price || "",
                              homeDeliveryPrice:
                                p.home_delivery_price ??
                                p.homeDeliveryPrice ??
                                prev.homeDeliveryPrice ??
                                "",
                              takewayPrice:
                                p.takeaway_price ??
                                p.takewayPrice ??
                                prev.takewayPrice ??
                                "",
                              diningPrice:
                                p.dining_price ??
                                p.diningPrice ??
                                prev.diningPrice ??
                                "",
                              roomOrderPrice:
                                p.room_order_price ??
                                p.roomOrderPrice ??
                                prev.roomOrderPrice ??
                                "",
                              carOrderPrice:
                                p.car_order_price ??
                                p.carOrderPrice ??
                                prev.carOrderPrice ??
                                "",
                              taxPercentage:
                                p.item_tax ??
                                p.taxPercentage ??
                                prev.taxPercentage ??
                                "",
                              id: p.id || prev.id,
                              // tie variant with this price row too
                              variantId:
                                p.variantId ||
                                prev.variantId ||
                                p.size_id ||
                                "",
                              variantName:
                                p.variantName ||
                                prev.variantName ||
                                p.size ||
                                "",
                            }));
                          }}
                        >
                          {(viewModalItem.prices || []).map((p, i) => (
                            <option key={i} value={i}>
                              {p.size || "(no size)"} — ₹{p.price}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Expiry (date) */}
                      <div className="mb-2">
                        <label className="form-label">
                          Expiry Date
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={editableDetails.expiryDate || ""}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              expiryDate: e.target.value, // YYYY-MM-DD
                            })
                          }
                        />
                        <small className="text-muted">
                          Only the date will be saved (format: yyyy/mm/dd).
                        </small>
                      </div>

                      {/* Price fields */}
                      <div className="mb-2">
                        <label className="form-label">
                          Selling Price
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={editableDetails.sellingPrice || ""}
                          onWheel={handleNumberWheel}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              sellingPrice: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">
                          Home Delivery Price
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={editableDetails.homeDeliveryPrice || ""}
                          onWheel={handleNumberWheel}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              homeDeliveryPrice: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">
                          Takeaway Price
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={editableDetails.takewayPrice || ""}
                          onWheel={handleNumberWheel}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              takewayPrice: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">
                          Dining Price
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={editableDetails.diningPrice || ""}
                          onWheel={handleNumberWheel}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              diningPrice: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">
                          Room Order Price
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={editableDetails.roomOrderPrice || ""}
                          onWheel={handleNumberWheel}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              roomOrderPrice: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">
                          Car Order Price
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={editableDetails.carOrderPrice || ""}
                          onWheel={handleNumberWheel}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              carOrderPrice: e.target.value,
                            })
                          }
                        />
                      </div>

                      {/* Tax */}
                      <div className="mb-2">
                        <label className="form-label">
                          Tax Percentage
                        </label>
                        <select
                          className="form-select"
                          value={editableDetails.taxPercentage ?? ""}
                          onChange={(e) =>
                            setEditableDetails({
                              ...editableDetails,
                              taxPercentage: Number(e.target.value),
                            })
                          }
                        >
                          <option value="" disabled>
                            Select tax %
                          </option>
                          <option value={0}>0</option>
                          <option value={5}>5</option>
                          <option value={12}>12</option>
                          <option value={18}>18</option>
                          <option value={28}>28</option>
                        </select>
                      </div>

                      {/* Variant info (read-only line) */}
                      <div className="mb-2">
                        <label className="form-label">
                          Variant Info
                        </label>
                        <input
                          className="form-control"
                          disabled
                          value={
                            editableDetails.variantName
                              ? `${editableDetails.variantName} (ID: ${editableDetails.variantId || "-"
                              })`
                              : "-"
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="row g-3">
                        <div className="col-12 col-md-6">

                          <div className="border rounded-3 p-3 bg-light">
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                              <div className="fw-semibold">Basic Details</div>
                              <span className="badge text-dark border bg-white">
                                ID: {editableDetails.item_id}
                              </span>
                            </div>

                            <div className="small text-muted">Name</div>
                            <div className="fw-semibold mb-2">
                              {editableDetails.item_name}
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                              <span className="badge text-dark border bg-white">
                                SubCat ID: {editableDetails.categoryId || "-"}
                              </span>
                              <span className="badge text-dark border bg-white">
                                Rack: {editableDetails.rackNo || viewModalItem.rackNumber || "-"}
                              </span>
                              <span className="badge text-dark border bg-white">
                                Expiry: {expiryDisplay || "-"}
                              </span>
                            </div>

                            <div className="mt-3 small text-muted">Sub-category</div>
                            <div>
                              {editableDetails.category || viewModalItem.sub_category || "-"}
                            </div>

                            <div className="mt-3 small text-muted">Variant</div>
                            <div>
                              {editableDetails.variantName || "-"}{" "}
                              {editableDetails.variantId ? (
                                <span className="text-muted">(ID: {editableDetails.variantId})</span>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="border rounded-3 p-3">
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                              <div className="fw-semibold">Selected Price Row</div>
                              <span className="badge text-dark border bg-light">
                                Tax: {editableDetails.taxPercentage ?? "-"}%
                              </span>
                            </div>

                            <div className="d-flex flex-wrap gap-2 mb-3">
                              <span className="badge bg-primary">
                                Size: {editableDetails.size || "(no size)"}
                              </span>
                            </div>

                            <div className="d-flex justify-content-between align-items-center py-1">
                              <span className="text-muted">Selling</span>
                              <span className="fw-semibold">₹{editableDetails.sellingPrice || "-"}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1">
                              <span className="text-muted">Home Delivery</span>
                              <span className="fw-semibold">₹{editableDetails.homeDeliveryPrice || "-"}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1">
                              <span className="text-muted">Takeaway</span>
                              <span className="fw-semibold">₹{editableDetails.takewayPrice || "-"}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1">
                              <span className="text-muted">Dining</span>
                              <span className="fw-semibold">₹{editableDetails.diningPrice || "-"}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1">
                              <span className="text-muted">Room Order</span>
                              <span className="fw-semibold">₹{editableDetails.roomOrderPrice || "-"}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center py-1">
                              <span className="text-muted">Car Order</span>
                              <span className="fw-semibold">₹{editableDetails.carOrderPrice || "-"}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {isEditingDetails && (
                <div className="text-end mt-3">
                  <button
                    className="btn btn-primary"
                    onClick={handleUpdateItem}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              )}

              <hr />

              {Array.isArray(viewModalItem.prices) && viewModalItem.prices.length > 0 && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0 fw-semibold">All Size Prices</h6>
                    <span className="small text-muted">
                      {viewModalItem.prices.length} {viewModalItem.prices.length === 1 ? "row" : "rows"}
                    </span>
                  </div>

                  <div className="d-flex flex-wrap gap-3">
                    {viewModalItem.prices.map((priceObj, idx) => (
                      <div
                        key={idx}
                        className="border rounded-3 p-3"
                        style={{ minWidth: 260, flex: "1 1 260px" }}
                      >
                        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                          <div className="fw-semibold">
                            {priceObj.size || "(no size)"}
                          </div>
                          <span className="badge text-dark border bg-light">
                            {priceObj.item_tax ?? "-"}% tax
                          </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted">Base</span>
                          <span className="fw-semibold">
                            {priceObj.formatted_price || (priceObj.price ? `₹${priceObj.price}` : "-")}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted">Home Delivery</span>
                          <span className="fw-semibold">
                            {priceObj.home_delivery_price ? `₹${priceObj.home_delivery_price}` : "-"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted">Takeaway</span>
                          <span className="fw-semibold">
                            {priceObj.takeaway_price ? `₹${priceObj.takeaway_price}` : "-"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted">Dining</span>
                          <span className="fw-semibold">
                            {priceObj.dining_price ? `₹${priceObj.dining_price}` : "-"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted">Room Order</span>
                          <span className="fw-semibold">
                            {priceObj.room_order_price ? `₹${priceObj.room_order_price}` : "-"}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center py-1">
                          <span className="text-muted">Car Order</span>
                          <span className="fw-semibold">
                            {priceObj.car_order_price ? `₹${priceObj.car_order_price}` : "-"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* AVAILABILITY TAB */}
          {viewModalTab === "availability" && (
            <form
              className="d-flex flex-column gap-3"
              onSubmit={(e) => handleSubmit(e, viewModalItem.item_id)}
            >
              <p className="text-muted">
                Configure availability for this item:
              </p>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="avail-home"
                  checked={
                    availabilityData[viewModalItem.item_id]
                      ?.homeDelivery || false
                  }
                  onChange={() =>
                    handleCheckboxChange(
                      viewModalItem.item_id,
                      "homeDelivery"
                    )
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="avail-home"
                >
                  Home Delivery
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="avail-take"
                  checked={
                    availabilityData[viewModalItem.item_id]
                      ?.takeaway || false
                  }
                  onChange={() =>
                    handleCheckboxChange(
                      viewModalItem.item_id,
                      "takeaway"
                    )
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="avail-take"
                >
                  Takeaway
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="avail-dine"
                  checked={
                    availabilityData[viewModalItem.item_id]
                      ?.dineIn || false
                  }
                  onChange={() =>
                    handleCheckboxChange(
                      viewModalItem.item_id,
                      "dineIn"
                    )
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="avail-dine"
                >
                  Dine-in
                </label>
              </div>
              <button
                className="btn btn-success mt-2"
                type="submit"
              >
                Save Availability
              </button>
            </form>
          )}

          {/* PRINTER TAB */}
          {viewModalTab === "printer" && (
            <>
              <p className="text-muted mb-2">
                Assign a printer to this item. This ensures the order
                slip is printed automatically for the selected section.
              </p>
              {printerLoading ? (
                <div className="text-center py-3">
                  Loading printers...
                </div>
              ) : (
                <select
                  className="form-select bg-light border-secondary"
                  onChange={(e) =>
                    setSelectedPrinter(
                      printerList.find(
                        (p) => p.printerCode === e.target.value
                      )
                    )
                  }
                >
                  <option value="">Select Printer</option>
                  {printerList.map((printer) => (
                    <option
                      key={printer.printerCode}
                      value={printer.printerCode}
                    >
                      {printer.printerName}-{printer.printerAlias}
                    </option>
                  ))}
                </select>
              )}
              <div className="d-flex gap-2 ">
                <button
                  className="btn btn-primary mt-3"
                  onClick={handleSavePrinterToItem}
                  disabled={!selectedPrinter}
                >
                  Save Printer
                </button>
                <div>
                  <button
                    className="btn btn-primary mt-3"
                    onClick={() => setAddPrinterModalOpen(true)}
                  >
                    Add Printer
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <button
          className="btn btn-outline-secondary"
          onClick={() => setViewModalItem(null)}
        >
          Close
        </button>
        <button
          className="btn btn-primary"
          onClick={() =>
            navigate(
              `/Orders/Catalogue/edit-ingredients/${viewModalItem.item_id}`,
              {
                state: {
                  name: viewModalItem.item_name,
                  photo: viewModalItem.photo,
                },
              }
            )
          }
        >
          Ingredients Edit
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CatalogueItemModal;
